import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

import { Toaster } from '@blattnercompany/ui'
import type { ToastIcons } from '@blattnercompany/ui'
import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import AppSidebar from '@/components/AppSidebar'
import ProjectSwitcher from '@/components/ProjectSwitcher'
import TopBar from '@/components/TopBar'
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import UserChip from '@/components/UserChip'
import { useCurrentUser } from '@/hooks/useCurrentUser'

// The kit ships inline glyphs so it carries no icon dependency; the app hands
// over its own set so a toast reads like the rest of the chrome. Severity
// glyphs are replaced, never removed -- they carry severity for anyone who
// cannot rely on the accent colour. Module-level so the reference is stable
// across renders. The kit sizes whatever <svg> lands in the slot to 16px and
// wraps it aria-hidden, so Lucide needs nothing extra.
const TOAST_ICONS: ToastIcons = {
  info: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  error: <CircleX />,
  dismiss: <X />,
}

type Props = {
  children: ReactNode
}

// SidebarProvider writes `sidebar_state` on every toggle (see ui/sidebar.tsx)
// but leaves reading it to the app — without this, defaultOpen wins on every
// reload and the collapse never persists.
function readSidebarStateCookie(): boolean {
  const entry = document.cookie
    .split('; ')
    .find((c) => c.startsWith('sidebar_state='))
  return entry ? entry.split('=')[1] === 'true' : true
}

/**
 * Persistent chrome on the shadcn sidebar system: top bar with the sidebar
 * trigger, AppSidebar (collapsible column / mobile Sheet), and the scrollable
 * main region. SidebarProvider owns open state (plus Ctrl/Cmd+B and cookie
 * persistence) and must wrap the TopBar so the trigger reaches its context.
 */
function AppShell({ children }: Props) {
  const user = useCurrentUser()
  // Read once on mount; the provider owns the state (and the cookie) after.
  const [initialSidebarOpen] = useState(readSidebarStateCookie)
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()

  // New screen: scroll back to the top.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [pathname])

  return (
    <SidebarProvider
      defaultOpen={initialSidebarOpen}
      className="app min-h-0 flex-col"
      style={{ '--sidebar-width': '232px' } as CSSProperties}
    >
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      {/* Trigger: no hover box — the icon itself brightens (same language
          as the sidebar's text-only hover). The bg-transparent/text
          overrides also undo the Button's built-in hover and expanded
          styling (bg-muted/dark text — unreadable on the dark top bar).
          size-11 keeps the invisible 44px hit area. */}
      <TopBar
        productName="Stagehand"
        tagline="Material Receiving & Tracking"
        leading={
          <SidebarTrigger
            aria-label="Toggle navigation"
            className="size-11 text-white hover:bg-transparent hover:text-(--blattner-light-blue) aria-expanded:bg-transparent aria-expanded:text-white aria-expanded:hover:bg-transparent aria-expanded:hover:text-(--blattner-light-blue)"
          />
        }
        afterProduct={<ProjectSwitcher />}
      >
        <UserChip user={user} />
      </TopBar>
      <div className="shell">
        <AppSidebar />
        {/* tabIndex lets the skip link move focus here. */}
        <main id="main-content" className="main" ref={mainRef} tabIndex={-1}>
          <div className="main__inner">{children}</div>
        </main>
      </div>
      {/* One per app. It portals to document.body, so it sits here for
          lifetime rather than layout — mounted once for every page, and
          outside `.main` so a toast is never clipped by the scroll owner.
          Top-centre anchors to the viewport, so App.css offsets the stack by
          --topbar-h to clear the sticky top bar. */}
      <Toaster position="top-center" icons={TOAST_ICONS} />
    </SidebarProvider>
  )
}

export default AppShell
